import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@/constants/routes";
import { useState } from "react";
import { catchAxiosError } from "@/lib/errors";
import { handleApiError } from "@/utils/errors";
import { toast } from "sonner";
import { authRepo } from "@/lib/repositories";

const formSchema = z.object({
  email: z.string().email(),
});

interface RequestResetFormProps {
  onSuccess: (email: string) => void;
}

const RequestResetForm = ({ onSuccess }: RequestResetFormProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true);
    const [error] = await catchAxiosError(
      authRepo.requestPasswordReset(data.email),
    );
    setIsLoading(false);
    if (error) {
      toast.error(handleApiError(error).message);
      return;
    }
    onSuccess(data.email);
  }

  return (
    <div className="w-full max-w-112.5 flex flex-col gap-12">
      <form id="request-reset-form" onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          <Controller
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="email-input">
                  {t("modules.auth.forgot_password.email_label")}
                </FieldLabel>
                <Input
                  {...field}
                  className="h-12"
                  id="email-input"
                  type="email"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
        </FieldGroup>
      </form>
      <div className="flex flex-col gap-4">
        <Button
          disabled={isLoading}
          type="submit"
          form="request-reset-form"
          className="h-16 text-lg"
        >
          {isLoading && <Loader className="animate-spin" />}
          {t("modules.auth.forgot_password.submit_request")}
        </Button>
        <Link
          className="underline text-blue-600 text-center text-sm"
          to={ROUTES.LOGIN}
        >
          {t("modules.auth.forgot_password.back_to_login")}
        </Link>
      </div>
    </div>
  );
};

export default RequestResetForm;
